"use client";

import { ExternalLink, Eye, RefreshCw, SkipForward } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";

import { useQuery } from "@tanstack/react-query";

import { Link } from "@/i18n/navigation";
import { extractApiErrorMessage } from "@/shared/api/error-handler";
import { api } from "@/shared/lib/api";
import { Button } from "@/shared/ui/buttons/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import type { ApiMatchDTO } from "@/entities/bracket/api/types";
import type { MatchTeamSide } from "@/entities/match/model/use-user-match-side";
import type { MatchUserRole } from "@/entities/bracket/lib/match-lifecycle";
import type { PreDraftStateDto } from "@/entities/predraft/model/types";

interface MatchPreDraftCardProps {
  match: Pick<
    ApiMatchDTO,
    | "status"
    | "phase"
    | "preDraftRoomId"
    | "preDraftBlueLink"
    | "preDraftRedLink"
    | "preDraftSpectatorLink"
  >;
  tournamentType: string | null | undefined;
  role: MatchUserRole;
  side: MatchTeamSide | null;
  /** Id матча — нужен организаторским действиям над комнатой (KAN-822). */
  matchId?: string;
  className?: string;
}

// Сверено с MatchPhase (Match.cs): LobbyPending → LobbyCreated → ChampionSelect → InGame
// → ResultUploaded → Confirmed. Карточка живёт до InGame — дальше драфт уже не нужен.
const VISIBLE_PHASES = new Set([
  "lobbypending",
  "lobbycreated",
  "championselect",
]);

/** Ключи Ui с названиями фаз драфта для карточки матча (KAN-820). */
const PHASE_LABEL_KEYS: Record<string, string> = {
  Phase1Bans: "litMatchPredraftCard.banyFaza1",
  Phase1Picks: "litMatchPredraftCard.pikiFaza1",
  Phase2Bans: "litMatchPredraftCard.banyFaza2",
  Phase2Picks: "litMatchPredraftCard.pikiFaza2",
  Complete: "litMatchPredraftCard.draftZavershen",
};

export function MatchPreDraftCard({
  match,
  tournamentType,
  role,
  side,
  matchId,
  className,
}: MatchPreDraftCardProps) {
  const tUi = useTranslations("Ui");
  const t = useTranslations("Match.preDraft");
  const [busy, setBusy] = useState(false);

  // KAN-820: карточка показывает, на чём стоит драфт. Без этого участник видел три
  // кнопки и не понимал, идёт ли что-то вообще и сколько осталось.
  const { data: roomState, dataUpdatedAt } = useQuery({
    queryKey: ["predraft", "state", match.preDraftRoomId],
    queryFn: async () => {
      const res = await api.get<PreDraftStateDto>(`/predraft/${match.preDraftRoomId}`);
      return res.data;
    },
    enabled: !!match.preDraftRoomId,
    // Ход длится 30 секунд по умолчанию: чаще обновлять смысла нет, реже — уже враньё.
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  // KAN-937: опрос раз в 10 с, а счётчик тикает локально — иначе он прыгает по 10 секунд.
  const [now, setNow] = useState(() => Date.now());
  const ticking = !!roomState && !roomState.isComplete && roomState.secondsLeft > 0;
  useEffect(() => {
    if (!ticking) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [ticking]);
  const secondsLeft = roomState
    ? Math.max(0, roomState.secondsLeft - Math.floor(Math.max(0, now - dataUpdatedAt) / 1000))
    : 0;

  if (tournamentType === "SoloOneVsOne") return null;
  if (!match.preDraftRoomId) return null;
  if (match.status !== "Scheduled") return null;
  const phase = String(match.phase ?? "").trim().toLowerCase();
  if (phase && !VISIBLE_PHASES.has(phase)) return null;

  const blueLink = match.preDraftBlueLink ?? undefined;
  const redLink = match.preDraftRedLink ?? undefined;
  const spectatorLink = match.preDraftSpectatorLink ?? undefined;

  const isMemberOfSide = role === "captain" || role === "participant";
  const mySideLink =
    side === "blue" ? blueLink : side === "red" ? redLink : undefined;
  const mySideLabel =
    side === "blue" ? t("openBlue") : side === "red" ? t("openRed") : null;

  // KAN-822: организатору — управление комнатой. Раньше с испорченной комнатой
  // (ссылка утекла, состав сменился, никто не зашёл) оставалось ждать дедлайна.
  const isOrganizer = role === "organizer";

  const runAction = async (path: string, successText: string, retryWithForce = false) => {
    if (!matchId || busy) return;
    setBusy(true);
    try {
      await api.post(`/matches/${matchId}/predraft/${path}`);
      toast.success(successText);
    } catch (error) {
      const message = extractApiErrorMessage(error, tUi("litMatchPredraftCard.neUdalosVypolnitDeystvie"));
      // Начатый драфт пересоздаём только с подтверждения: иначе команда узнает
      // о потере банов и пиков по опустевшему экрану.
      if (retryWithForce && message.includes("Подтвердите")) {
        if (window.confirm(`${message}\n\nПересоздать комнату и стереть сделанные ходы?`)) {
          try {
            await api.post(`/matches/${matchId}/predraft/${path}?force=true`);
            toast.success(successText);
            return;
          } catch (forced) {
            toast.error(extractApiErrorMessage(forced, tUi("litMatchPredraftCard.neUdalosPeresozdatKomnatu")));
            return;
          }
        }
        return;
      }
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className={`border-primary/30 ${className ?? ""}`.trim()}>
      <CardHeader>
        <CardTitle>{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      {roomState && (
        <div className="text-muted-foreground px-6 pb-2 text-sm">
          {PHASE_LABEL_KEYS[roomState.phase] ? tUi(PHASE_LABEL_KEYS[roomState.phase]!) : roomState.phase}
          {!roomState.isComplete && secondsLeft > 0 && (
            <span className="text-foreground ml-2 font-mono tabular-nums">
              {secondsLeft} с
            </span>
          )}
          {roomState.totalGames > 1 && (
            <span className="ml-2">
              · игра {roomState.gameNumber} из {roomState.totalGames}
            </span>
          )}
        </div>
      )}
      <CardContent className="flex flex-col gap-3 sm:flex-row">
        {isMemberOfSide && mySideLink && mySideLabel && (
          <Button asChild size="lg" className="w-full sm:w-auto">
            <Link href={mySideLink} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              {mySideLabel}
            </Link>
          </Button>
        )}
        {isMemberOfSide && !mySideLink && (
          <p className="text-muted-foreground text-xs sm:self-center">
            {tUi("matchPredraftCard.storonaEscheNeOpredelena")}
          </p>
        )}
        {isMemberOfSide && mySideLink && role !== "captain" && (
          <p className="text-muted-foreground text-xs sm:self-center">
            {t("readOnlyHint")}
          </p>
        )}
        {isOrganizer && matchId && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => runAction("recreate", tUi("litMatchPredraftCard.komnataPeresozdanaSsylkiObnovleny"), true)}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {tUi("matchPredraftCard.peresozdat")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => runAction("complete", tUi("litMatchPredraftCard.draftZavershenMatchV"))}
            >
              <SkipForward className="mr-2 h-4 w-4" />
              {tUi("matchPredraftCard.zavershitDraft")}
            </Button>
          </>
        )}
        {spectatorLink && (
          <Button
            asChild
            variant="outline"
            size={isMemberOfSide ? "default" : "lg"}
            className="w-full sm:w-auto"
          >
            <Link
              href={spectatorLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Eye className="mr-2 h-4 w-4" />
              {t("openSpectator")}
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
